import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import ROUTES from "../routes";
import { EXTERNAL_LINKS } from "../constants/externalLinks";
import logo from "../assets/logo.png";
import fbIcon from "../assets/Icons/fb-Icon.png";
import instaIcon from "../assets/Icons/insta-icon.png";

const quickLinks = [
  { label: "Home", to: ROUTES.HOME },
  { label: "Meet Our Vendors", to: ROUTES.MEET_OUR_VENDORS },
  { label: "Become a Vendor", to: ROUTES.BECOME_A_VENDOR },
  { label: "See Events", to: ROUTES.SEE_EVENTS },
  { label: "Our Story", to: ROUTES.OUR_STORY },
  { label: "Plan Your Visit", to: ROUTES.PLAN_VISIT },
];

const rentalLinks = [
  { label: "Rental Spaces", to: ROUTES.RENTAL_SPACES },
  { label: "Main Market Hall", to: ROUTES.MainMarketHall },
  { label: "Workshop Room", to: ROUTES.WorkshopRoom },
  { label: "Commercial Kitchen", to: ROUTES.Kitchen },
  { label: "Contact Us", to: ROUTES.CONTACT_US },
  { label: "FAQs", to: ROUTES.FAQ },
];

const columnVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: "easeOut" as const, delay: i * 0.1 },
  }),
};

export default function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer style={{ position: "relative" }}>
      {/* Top wave — Reviews (dark navy) flows seamlessly into footer */}
      <div style={{ lineHeight: 0, background: "#081422" }}>
        <svg
          viewBox="0 0 1440 72"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
          style={{ width: "100%", display: "block", height: "72px" }}
        >
          <path
            d="M0 0V38C200 68 460 78 720 52C980 26 1220 48 1440 38V0H0Z"
            fill="#080f1a"
          />
        </svg>
      </div>

      {/* Main footer body */}
      <div
        style={{
          background: "linear-gradient(180deg, #080f1a 0%, #0b1525 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Background grid */}
        <div className="ft-grid" />
        {/* Glow blobs */}
        <div className="ft-blob ft-blob-1" />
        <div className="ft-blob ft-blob-2" />

        <div
          style={{
            maxWidth: "1200px",
            margin: "0 auto",
            padding: "clamp(48px, 7vw, 80px) clamp(20px, 4vw, 40px) clamp(32px, 5vw, 56px)",
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Newsletter strip */}
          <motion.div
            custom={0}
            variants={columnVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            style={{
              background: "rgba(109,199,68,0.06)",
              border: "1px solid rgba(109,199,68,0.15)",
              borderRadius: 20,
              padding: "clamp(1.5rem, 3vw, 2rem) clamp(1.5rem, 4vw, 2.5rem)",
              marginBottom: "clamp(32px, 5vw, 48px)",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "1.5rem",
            }}
          >
            <div style={{ flex: "1 1 300px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: "1.4rem" }}>📰</span>
                <span style={{ color: "#6dc744", fontWeight: 700, fontSize: "0.78rem", letterSpacing: "1.6px", textTransform: "uppercase", fontFamily: "'Montserrat', sans-serif" }}>
                  Quarterly Newsletter
                </span>
              </div>
              <h3 style={{ color: "#fff", fontWeight: 800, fontSize: "clamp(1.1rem, 2.5vw, 1.4rem)", margin: "0 0 6px", fontFamily: "'Montserrat', sans-serif" }}>
                Read Our Community Newsletter
              </h3>
              <p style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.88rem", margin: 0, lineHeight: 1.6 }}>
                Vendor stories, co-op updates, seasonal recipes &amp; more — published quarterly, always free.
              </p>
            </div>
            <Link
              to={ROUTES.NEWSLETTER}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                background: "linear-gradient(135deg, #c6252d, #a01e25)",
                color: "white",
                borderRadius: 999,
                padding: "0.75rem 1.8rem",
                fontWeight: 700,
                fontSize: "0.95rem",
                textDecoration: "none",
                boxShadow: "0 4px 20px rgba(198,37,45,0.3)",
                whiteSpace: "nowrap",
                flexShrink: 0,
                transition: "transform 0.2s, box-shadow 0.2s",
              }}
              className="ft-newsletter-btn"
            >
              Browse Issues →
            </Link>
          </motion.div>

          {/* Columns grid */}
          <div className="ft-columns">
            {/* Column 1 — Brand */}
            <motion.div
              custom={0}
              variants={columnVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              style={{ gridColumn: "span 1" }}
            >
              <img
                src={logo}
                alt="St. John's Farmers' Market"
                style={{
                  width: "clamp(64px, 10vw, 88px)",
                  height: "auto",
                  marginBottom: "16px",
                  borderRadius: "12px",
                  filter: "drop-shadow(0 4px 16px rgba(109,199,68,0.2))",
                }}
              />
              <h3
                style={{
                  color: "#fff",
                  fontFamily: "'Montserrat', sans-serif",
                  fontWeight: 800,
                  fontSize: "1.05rem",
                  margin: "0 0 8px",
                  letterSpacing: "-0.3px",
                }}
              >
                St. John's
                <br />
                Farmers' Market
              </h3>
              <p
                style={{
                  color: "rgba(255,255,255,0.45)",
                  fontSize: "0.88rem",
                  lineHeight: 1.65,
                  margin: "0 0 24px",
                  maxWidth: "220px",
                }}
              >
                Fresh. Local. Community. Every Saturday at 245 Freshwater Rd,
                St. John's, NL.
              </p>

              {/* Social icons */}
              <div style={{ display: "flex", gap: "12px" }}>
                {[
                  { href: EXTERNAL_LINKS.facebook, icon: fbIcon, label: "Facebook" },
                  { href: EXTERNAL_LINKS.instagram, icon: instaIcon, label: "Instagram" },
                ].map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={s.label}
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "10px",
                      background: "rgba(255,255,255,0.07)",
                      border: "1px solid rgba(255,255,255,0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "background 0.2s, border-color 0.2s",
                    }}
                    className="ft-social-btn"
                  >
                    <img src={s.icon} alt={s.label} width={18} height={18} />
                  </a>
                ))}
              </div>
            </motion.div>

            {/* Column 2 — Quick Links */}
            <motion.div
              custom={1}
              variants={columnVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <h4 className="ft-col-heading">Explore</h4>
              <ul className="ft-link-list">
                {quickLinks.map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} className="ft-link">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Column 3 — Rentals & More */}
            <motion.div
              custom={2}
              variants={columnVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <h4 className="ft-col-heading">Spaces & Info</h4>
              <ul className="ft-link-list">
                {rentalLinks.map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} className="ft-link">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Column 4 — Hours & Location */}
            <motion.div
              custom={3}
              variants={columnVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <h4 className="ft-col-heading">Visit Us</h4>

              {/* Hours card */}
              <div className="ft-info-card">
                <div className="ft-info-card-label">Market Hours</div>
                <div className="ft-info-card-value">Saturdays</div>
                <div className="ft-info-card-sub">9:00 AM – 4:00 PM · Year-Round</div>
              </div>

              {/* Address card */}
              <div className="ft-info-card" style={{ marginTop: "12px" }}>
                <div className="ft-info-card-label">Location</div>
                <div className="ft-info-card-value">245 Freshwater Rd</div>
                <div className="ft-info-card-sub">St. John's, NL A1B 1B3</div>
                <a
                  href={EXTERNAL_LINKS.marketGoogleMap}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "5px",
                    color: "#6dc744",
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    textDecoration: "none",
                    marginTop: "8px",
                    fontFamily: "'Montserrat', sans-serif",
                  }}
                >
                  Get Directions →
                </a>
              </div>
            </motion.div>
          </div>

          {/* Divider */}
          <div
            style={{
              height: "1px",
              background: "rgba(255,255,255,0.07)",
              margin: "clamp(32px, 5vw, 48px) 0 clamp(20px, 3vw, 28px)",
            }}
          />

          {/* Bottom bar */}
          <div className="ft-bottom-bar">
            <span style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.82rem" }}>
              © {year} St. John's Farmers' Market. All rights reserved.
            </span>
            <span style={{ color: "rgba(255,255,255,0.25)", fontSize: "0.82rem" }}>
              Developed by{" "}
              <a
                href="https://youssefmohamed.ca/"
                target="_blank"
                rel="noreferrer"
                style={{
                  color: "#6dc744",
                  textDecoration: "none",
                  fontWeight: 600,
                  transition: "opacity 0.2s",
                }}
                className="ft-dev-link"
              >
                Youssef Mohamed
              </a>
            </span>
          </div>
        </div>
      </div>

      <style>{`
        .ft-grid {
          position: absolute; inset: 0;
          background-image:
            linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px);
          background-size: 44px 44px;
          pointer-events: none;
        }
        .ft-blob {
          position: absolute; border-radius: 50%;
          pointer-events: none; filter: blur(90px);
        }
        .ft-blob-1 {
          width: 480px; height: 480px;
          background: rgba(109,199,68,0.05);
          top: -150px; right: -100px;
        }
        .ft-blob-2 {
          width: 360px; height: 360px;
          background: rgba(45,106,20,0.1);
          bottom: -80px; left: -80px;
        }
        .ft-columns {
          display: grid;
          grid-template-columns: 1.4fr 1fr 1fr 1.2fr;
          gap: clamp(24px, 4vw, 48px);
        }
        .ft-col-heading {
          color: #fff;
          font-family: 'Montserrat', sans-serif;
          font-weight: 700;
          font-size: 0.82rem;
          letter-spacing: 1.8px;
          text-transform: uppercase;
          margin: 0 0 20px;
        }
        .ft-link-list {
          list-style: none; padding: 0; margin: 0;
          display: flex; flex-direction: column; gap: 12px;
        }
        .ft-link {
          color: rgba(255,255,255,0.5);
          text-decoration: none;
          font-size: 0.9rem;
          font-family: 'Montserrat', sans-serif;
          transition: color 0.2s;
          display: inline-flex; align-items: center; gap: 6px;
        }
        .ft-link::before {
          content: '';
          width: 4px; height: 4px;
          border-radius: 50%;
          background: rgba(109,199,68,0.4);
          flex-shrink: 0;
          transition: background 0.2s;
        }
        .ft-link:hover { color: #6dc744; }
        .ft-link:hover::before { background: #6dc744; }
        .ft-info-card {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 14px;
          padding: 16px 18px;
        }
        .ft-info-card-label {
          color: rgba(109,199,68,0.7);
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          font-family: 'Montserrat', sans-serif;
          margin-bottom: 4px;
        }
        .ft-info-card-value {
          color: #fff;
          font-size: 1rem;
          font-weight: 700;
          font-family: 'Montserrat', sans-serif;
          margin-bottom: 2px;
        }
        .ft-info-card-sub {
          color: rgba(255,255,255,0.45);
          font-size: 0.82rem;
          line-height: 1.5;
        }
        .ft-bottom-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 8px;
        }
        .ft-social-btn:hover {
          background: rgba(109,199,68,0.12) !important;
          border-color: rgba(109,199,68,0.3) !important;
        }
        .ft-dev-link:hover { opacity: 0.75; }
        .ft-newsletter-btn:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(198,37,45,0.45) !important; }
        @media (max-width: 900px) {
          .ft-columns {
            grid-template-columns: 1fr 1fr;
          }
        }
        @media (max-width: 540px) {
          .ft-columns {
            grid-template-columns: 1fr;
          }
          .ft-bottom-bar {
            flex-direction: column;
            text-align: center;
          }
        }
      `}</style>
    </footer>
  );
}
